<script setup lang="ts">
import { ref, nextTick, watch } from 'vue';
import { useSvgComponent } from '@/Composables/App/useSvgComponent';

const props = defineProps<{
  name: string;
  modifier?: string;
}>();

const icons = import.meta.glob('@/Assets/icons/*.svg', {
  import: 'default',
});

const {
  component: iconComponent,
  isLoading,
  load: loadIcon,
} = useSvgComponent(icons, 'Icon', (iconName) => `/${iconName}.svg`);

const svgRef = ref<HTMLElement | null>(null);
const svg = ref<SVGElement | null>(null);
const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

watch(
  () => props.name,
  async (newName) => {
    await loadIcon(newName);
    await nextTick();
    let tries = 0;
    while (svgRef.value && !svgRef.value.querySelector('svg') && tries < 1000) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(25);
      tries++;
    }
    svg.value = (svgRef.value?.querySelector('svg') as SVGElement | null) ?? null;
  },
  { immediate: true },
);

defineExpose({
  svg,
});
</script>

<template>
  <i ref="svgRef" class="icon" :class="props.modifier" aria-hidden="true">
    <component
      :is="iconComponent"
      v-if="iconComponent && !isLoading"
      class="icon__svg"
      aria-hidden="true"
    />
  </i>
</template>

<style lang="scss">
@import '@/Scss/Icon/Components/_icon.scss';
</style>
